import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { XMarkIcon, BuildingOfficeIcon, MapPinIcon, PhoneIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { Accommodation } from '../../types';
import { FormField, Input, Select, Textarea, FormCard, DateInput } from '../../components/Forms';

interface AccommodationFormProps {
  accommodation?: Accommodation;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (accommodation: Accommodation) => void;
}

interface AccommodationFormData {
  name: string;
  location: 'مكة المكرمة' | 'المدينة المنورة';
  address: {
    street: string;
    district: string;
    nearbyLandmarks: string[];
    distanceToHaram: number;
    coordinates: {
      latitude: number;
      longitude: number;
    };
  };
  contact: {
    phone: string;
    mobile: string;
    email: string;
    manager: {
      name: string;
      phone: string;
    };
  };
  rooms: {
    roomNumber: string;
    type: 'family' | 'men' | 'women';
    capacity: number;
    pricePerPerson: number;
    amenities: string[];
    floor: number;
    bedType: 'single' | 'double' | 'bunk' | 'sofa';
  }[];
  contract: {
    startDate: string;
    endDate: string;
    pricePerNight: number;
    totalRooms: number;
    terms: string;
    paymentTerms: string;
  };
  services: {
    meals: {
      breakfast: boolean;
      lunch: boolean;
      dinner: boolean;
      cost: number;
    };
    transportation: {
      toHaram: boolean;
      cost: number;
    };
    laundry: {
      available: boolean;
      cost: number;
    };
    cleaning: {
      daily: boolean;
      cost: number;
    };
  };
  facilities: string[];
  rating: {
    overall: number;
    cleanliness: number;
    location: number;
    service: number;
  };
  isActive: boolean;
}

const AccommodationForm: React.FC<AccommodationFormProps> = ({ accommodation, isOpen, onClose, onSuccess }) => {
  const isEditing = !!accommodation;
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<AccommodationFormData>({
    name: '',
    location: 'مكة المكرمة',
    address: {
      street: '',
      district: '',
      nearbyLandmarks: [],
      distanceToHaram: 0,
      coordinates: {
        latitude: 0,
        longitude: 0
      }
    },
    contact: {
      phone: '',
      mobile: '',
      email: '',
      manager: {
        name: '',
        phone: ''
      }
    },
    rooms: [],
    contract: {
      startDate: '',
      endDate: '',
      pricePerNight: 0,
      totalRooms: 0,
      terms: '',
      paymentTerms: ''
    },
    services: {
      meals: {
        breakfast: false,
        lunch: false,
        dinner: false,
        cost: 0
      },
      transportation: {
        toHaram: false,
        cost: 0
      },
      laundry: {
        available: false,
        cost: 0
      },
      cleaning: {
        daily: true,
        cost: 0
      }
    },
    facilities: [],
    rating: {
      overall: 3,
      cleanliness: 3,
      location: 3,
      service: 3
    },
    isActive: true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [landmarks, setLandmarks] = useState<string>('');

  // Initialize form data when editing
  useEffect(() => {
    if (isEditing && accommodation) {
      setFormData({
        name: accommodation.name,
        location: accommodation.location,
        address: {
          street: accommodation.address?.street || '',
          district: accommodation.address?.district || '',
          nearbyLandmarks: accommodation.address?.nearbyLandmarks || [],
          distanceToHaram: accommodation.address?.distanceToHaram || 0,
          coordinates: {
            latitude: accommodation.address?.coordinates?.latitude || 0,
            longitude: accommodation.address?.coordinates?.longitude || 0
          }
        },
        contact: {
          phone: accommodation.contact.phone,
          mobile: accommodation.contact.mobile || '',
          email: accommodation.contact.email || '',
          manager: {
            name: accommodation.contact.manager?.name || '',
            phone: accommodation.contact.manager?.phone || ''
          }
        },
        rooms: accommodation.rooms?.map(room => ({
          roomNumber: room.roomNumber,
          type: room.type,
          capacity: room.capacity,
          pricePerPerson: room.pricePerPerson,
          amenities: room.amenities || [],
          floor: room.floor || 1,
          bedType: room.bedType || 'single'
        })) || [],
        contract: {
          startDate: new Date(accommodation.contract.startDate).toISOString().split('T')[0],
          endDate: new Date(accommodation.contract.endDate).toISOString().split('T')[0],
          pricePerNight: accommodation.contract.pricePerNight,
          totalRooms: accommodation.contract.totalRooms || 0,
          terms: accommodation.contract.terms || '',
          paymentTerms: accommodation.contract.paymentTerms || ''
        },
        services: {
          meals: {
            breakfast: accommodation.services?.meals?.breakfast || false,
            lunch: accommodation.services?.meals?.lunch || false,
            dinner: accommodation.services?.meals?.dinner || false,
            cost: accommodation.services?.meals?.cost || 0
          },
          transportation: {
            toHaram: accommodation.services?.transportation?.toHaram || false,
            cost: accommodation.services?.transportation?.cost || 0
          },
          laundry: {
            available: accommodation.services?.laundry?.available || false,
            cost: accommodation.services?.laundry?.cost || 0
          },
          cleaning: {
            daily: accommodation.services?.cleaning?.daily || true,
            cost: accommodation.services?.cleaning?.cost || 0
          }
        },
        facilities: accommodation.facilities || [],
        rating: {
          overall: accommodation.rating?.overall || 3,
          cleanliness: accommodation.rating?.cleanliness || 3,
          location: accommodation.rating?.location || 3,
          service: accommodation.rating?.service || 3
        },
        isActive: accommodation.isActive
      });
      
      setLandmarks(accommodation.address?.nearbyLandmarks?.join(', ') || '');
    }
  }, [isEditing, accommodation]);

  // Create/Update mutations
  const createMutation = useMutation({
    mutationFn: (data: Partial<Accommodation>) => apiService.createAccommodation(data),
    onSuccess: (newAccommodation) => {
      queryClient.invalidateQueries({ queryKey: ['accommodations'] });
      onSuccess?.(newAccommodation);
      onClose();
      resetForm();
    },
    onError: (error: any) => {
      console.error('Create accommodation error:', error);
      setErrors({ submit: 'حدث خطأ أثناء إنشاء المنشأة' });
    }
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Accommodation>) => apiService.updateAccommodation(accommodation!._id, data),
    onSuccess: (updatedAccommodation) => {
      queryClient.invalidateQueries({ queryKey: ['accommodations'] });
      onSuccess?.(updatedAccommodation);
      onClose();
    },
    onError: (error: any) => {
      console.error('Update accommodation error:', error);
      setErrors({ submit: 'حدث خطأ أثناء تحديث المنشأة' });
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      location: 'مكة المكرمة',
      address: {
        street: '',
        district: '',
        nearbyLandmarks: [],
        distanceToHaram: 0,
        coordinates: { latitude: 0, longitude: 0 }
      },
      contact: {
        phone: '',
        mobile: '',
        email: '',
        manager: { name: '', phone: '' }
      },
      rooms: [],
      contract: {
        startDate: '',
        endDate: '',
        pricePerNight: 0,
        totalRooms: 0,
        terms: '',
        paymentTerms: ''
      },
      services: {
        meals: { breakfast: false, lunch: false, dinner: false, cost: 0 },
        transportation: { toHaram: false, cost: 0 },
        laundry: { available: false, cost: 0 },
        cleaning: { daily: true, cost: 0 }
      },
      facilities: [],
      rating: { overall: 3, cleanliness: 3, location: 3, service: 3 },
      isActive: true
    });
    setLandmarks('');
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'اسم المنشأة مطلوب';
    }

    if (!formData.contact.phone.trim()) {
      newErrors.phone = 'رقم الهاتف مطلوب';
    }

    if (!formData.contract.startDate) {
      newErrors.startDate = 'تاريخ بداية العقد مطلوب';
    }

    if (!formData.contract.endDate) {
      newErrors.endDate = 'تاريخ نهاية العقد مطلوب';
    }

    if (formData.contract.pricePerNight <= 0) {
      newErrors.pricePerNight = 'سعر الليلة يجب أن يكون أكبر من صفر';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Prepare data for submission
    const accommodationData = {
      ...formData,
      address: {
        ...formData.address,
        nearbyLandmarks: landmarks.split(',').map(l => l.trim()).filter(l => l)
      },
      contract: {
        ...formData.contract,
        startDate: new Date(formData.contract.startDate),
        endDate: new Date(formData.contract.endDate)
      }
    };

    if (isEditing) {
      updateMutation.mutate(accommodationData);
    } else {
      createMutation.mutate(accommodationData);
    }
  };

  const addRoom = () => {
    setFormData(prev => ({
      ...prev,
      rooms: [...prev.rooms, {
        roomNumber: '',
        type: 'family',
        capacity: 2,
        pricePerPerson: 0,
        amenities: [],
        floor: 1,
        bedType: 'single'
      }]
    }));
  };

  const removeRoom = (index: number) => {
    setFormData(prev => ({
      ...prev,
      rooms: prev.rooms.filter((_, i) => i !== index)
    }));
  };

  const updateRoom = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      rooms: prev.rooms.map((room, i) => 
        i === index ? { ...room, [field]: value } : room
      )
    }));
  };

  const handleFacilityChange = (facility: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      facilities: checked 
        ? [...prev.facilities, facility]
        : prev.facilities.filter(f => f !== facility)
    }));
  };

  if (!isOpen) return null;

  const availableFacilities = [
    { value: 'parking', label: 'موقف سيارات' },
    { value: 'elevator', label: 'مصعد' },
    { value: 'reception', label: 'استقبال 24 ساعة' },
    { value: 'lobby', label: 'صالة انتظار' },
    { value: 'prayer_room', label: 'مصلى' },
    { value: 'restaurant', label: 'مطعم' },
    { value: 'shop', label: 'متجر' }
  ];

  const availableAmenities = [
    { value: 'AC', label: 'تكييف' },
    { value: 'TV', label: 'تلفزيون' },
    { value: 'WiFi', label: 'واي فاي' },
    { value: 'Fridge', label: 'ثلاجة' },
    { value: 'Bathroom', label: 'حمام خاص' },
    { value: 'Balcony', label: 'شرفة' },
    { value: 'Kitchen', label: 'مطبخ' }
  ];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <BuildingOfficeIcon className="w-6 h-6 text-primary-600 ml-3" />
            <h3 className="text-lg font-medium text-gray-900">
              {isEditing ? 'تعديل المنشأة' : 'إضافة منشأة جديدة'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <FormCard title="المعلومات الأساسية" icon={<BuildingOfficeIcon className="w-5 h-5 text-primary-600" />}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField label="اسم المنشأة" required error={errors.name}>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="مثال: فندق الحرمين"
                  />
                </FormField>

                <FormField label="المدينة" required>
                  <Select
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value as any }))}
                    options={[
                      { value: 'مكة المكرمة', label: 'مكة المكرمة' },
                      { value: 'المدينة المنورة', label: 'المدينة المنورة' }
                    ]}
                  />
                </FormField>
              </div>

              <div className="mt-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="mr-2 text-sm text-gray-700">المنشأة نشطة</span>
                </label>
              </div>
            </FormCard>

            {/* Address Information */}
            <FormCard title="معلومات العنوان" icon={<MapPinIcon className="w-5 h-5 text-primary-600" />}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField label="الشارع">
                  <Input
                    value={formData.address.street}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      address: { ...prev.address, street: e.target.value }
                    }))}
                    placeholder="مثال: شارع الملك عبدالعزيز"
                  />
                </FormField>

                <FormField label="الحي">
                  <Input
                    value={formData.address.district}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      address: { ...prev.address, district: e.target.value }
                    }))}
                    placeholder="مثال: العزيزية"
                  />
                </FormField>

                <FormField label="المسافة إلى الحرم (متر)">
                  <Input
                    type="number"
                    value={formData.address.distanceToHaram}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      address: { ...prev.address, distanceToHaram: parseInt(e.target.value) || 0 }
                    }))}
                    placeholder="0"
                  />
                </FormField>
              </div>

              <div className="mt-4">
                <FormField label="المعالم القريبة (مفصولة بفاصلة)">
                  <Textarea
                    value={landmarks}
                    onChange={(e) => setLandmarks(e.target.value)}
                    placeholder="مثال: الحرم المكي، مول العرب، مستشفى الملك فيصل"
                    rows={2}
                  />
                </FormField>
              </div>
            </FormCard>

            {/* Contact Information */}
            <FormCard title="معلومات الاتصال" icon={<PhoneIcon className="w-5 h-5 text-primary-600" />}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField label="رقم الهاتف" required error={errors.phone}>
                  <Input
                    value={formData.contact.phone}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      contact: { ...prev.contact, phone: e.target.value }
                    }))}
                    placeholder="012xxxxxxx"
                  />
                </FormField>

                <FormField label="رقم الجوال">
                  <Input
                    value={formData.contact.mobile}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      contact: { ...prev.contact, mobile: e.target.value }
                    }))}
                    placeholder="05xxxxxxxx"
                  />
                </FormField>

                <FormField label="البريد الإلكتروني">
                  <Input
                    type="email"
                    value={formData.contact.email}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      contact: { ...prev.contact, email: e.target.value }
                    }))}
                    placeholder="info@hotel.com"
                  />
                </FormField>
              </div>

              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-900 mb-4">معلومات المدير</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="اسم المدير">
                    <Input
                      value={formData.contact.manager.name}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        contact: { 
                          ...prev.contact, 
                          manager: { ...prev.contact.manager, name: e.target.value }
                        }
                      }))}
                      placeholder="الاسم الكامل"
                    />
                  </FormField>

                  <FormField label="رقم جوال المدير">
                    <Input
                      value={formData.contact.manager.phone}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        contact: { 
                          ...prev.contact, 
                          manager: { ...prev.contact.manager, phone: e.target.value }
                        }
                      }))}
                      placeholder="05xxxxxxxx"
                    />
                  </FormField>
                </div>
              </div>
            </FormCard>

            {/* Contract Information */}
            <FormCard title="معلومات العقد" icon={<BuildingOfficeIcon className="w-5 h-5 text-primary-600" />}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <FormField error={errors.startDate}>
                  <DateInput
                    label={
                      <span>
                        تاريخ بداية العقد <span className="text-red-500">*</span>
                      </span>
                    }
                    compactToggle
                    value={formData.contract.startDate}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      contract: { ...prev.contract, startDate: e.target.value }
                    }))}
                    error={!!errors.startDate}
                  />
                </FormField>

                <FormField error={errors.endDate}>
                  <DateInput
                    label={
                      <span>
                        تاريخ نهاية العقد <span className="text-red-500">*</span>
                      </span>
                    }
                    compactToggle
                    value={formData.contract.endDate}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      contract: { ...prev.contract, endDate: e.target.value }
                    }))}
                    error={!!errors.endDate}
                  />
                </FormField>

                <FormField label="سعر الليلة (ر.س)" required error={errors.pricePerNight}>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.contract.pricePerNight}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      contract: { ...prev.contract, pricePerNight: parseFloat(e.target.value) || 0 }
                    }))}
                  />
                </FormField>

                <FormField label="إجمالي الغرف">
                  <Input
                    type="number"
                    value={formData.contract.totalRooms}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      contract: { ...prev.contract, totalRooms: parseInt(e.target.value) || 0 }
                    }))}
                  />
                </FormField>
              </div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField label="شروط العقد">
                  <Textarea
                    value={formData.contract.terms}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      contract: { ...prev.contract, terms: e.target.value }
                    }))}
                    placeholder="شروط وأحكام العقد"
                    rows={3}
                  />
                </FormField>

                <FormField label="شروط الدفع">
                  <Textarea
                    value={formData.contract.paymentTerms}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      contract: { ...prev.contract, paymentTerms: e.target.value }
                    }))}
                    placeholder="شروط وطرق الدفع"
                    rows={3}
                  />
                </FormField>
              </div>
            </FormCard>

            {/* Facilities */}
            <FormCard title="المرافق والخدمات" icon={<UserGroupIcon className="w-5 h-5 text-primary-600" />}>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {availableFacilities.map((facility) => (
                  <label key={facility.value} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.facilities.includes(facility.value)}
                      onChange={(e) => handleFacilityChange(facility.value, e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="mr-2 text-sm text-gray-700">{facility.label}</span>
                  </label>
                ))}
              </div>

              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-900 mb-4">الخدمات الإضافية</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.services.meals.breakfast}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            services: { 
                              ...prev.services, 
                              meals: { ...prev.services.meals, breakfast: e.target.checked }
                            }
                          }))}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="mr-2 text-sm text-gray-700">إفطار</span>
                      </label>
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.services.transportation.toHaram}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            services: { 
                              ...prev.services, 
                              transportation: { ...prev.services.transportation, toHaram: e.target.checked }
                            }
                          }))}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="mr-2 text-sm text-gray-700">نقل للحرم</span>
                      </label>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.services.laundry.available}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            services: { 
                              ...prev.services, 
                              laundry: { ...prev.services.laundry, available: e.target.checked }
                            }
                          }))}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="mr-2 text-sm text-gray-700">غسيل ملابس</span>
                      </label>
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.services.cleaning.daily}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            services: { 
                              ...prev.services, 
                              cleaning: { ...prev.services.cleaning, daily: e.target.checked }
                            }
                          }))}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="mr-2 text-sm text-gray-700">تنظيف يومي</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </FormCard>
          </div>

          {/* Error Message */}
          {errors.submit && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-600 text-sm">{errors.submit}</p>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 space-x-reverse mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="btn-primary"
            >
              {createMutation.isPending || updateMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2"></div>
                  {isEditing ? 'جاري التحديث...' : 'جاري الحفظ...'}
                </>
              ) : (
                isEditing ? 'تحديث المنشأة' : 'إضافة المنشأة'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AccommodationForm; 